/**
 * evTariffMapper — normalise `/v3/evTariffs` payloads for the Price Registry UI.
 */

export const TARIFF_TYPE_OPTIONS = [
    { value: 'DC_FAST', label: 'DC Fast' },
    { value: 'DC_PUBLIC', label: 'DC Public' },
    { value: 'AC_PUBLIC', label: 'AC Public' },
    { value: 'AC_HOME', label: 'AC Home' },
];

const parseRowDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

export const hasEvFilters = ({ tariffType, dateFrom, dateTo } = {}) => (
    (Boolean(tariffType) && tariffType !== 'ALL')
    || Boolean(dateFrom)
    || Boolean(dateTo)
);

export const filterEvTariffRows = (rows, { tariffType, dateFrom, dateTo } = {}) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const fromTs = parseRowDate(dateFrom);
    const toTs = dateTo ? (() => {
        const end = new Date(dateTo);
        if (Number.isNaN(end.getTime())) return null;
        end.setHours(23, 59, 59, 999);
        return end.getTime();
    })() : null;

    return rows.filter((row) => {
        if (tariffType && tariffType !== 'ALL' && row.tariff !== tariffType) return false;
        const rowTs = parseRowDate(row.effectiveFrom);
        if (rowTs === null) return false;
        if (fromTs !== null && rowTs < fromTs) return false;
        if (toTs !== null && rowTs > toTs) return false;
        return true;
    });
};

const isCurrentEvRow = (row) => {
    if (typeof row?.isCurrent === 'boolean') return row.isCurrent;
    return row?.effectiveTo == null || row?.effectiveTo === '';
};

export const isClosedEvTariffRow = (row) => !isCurrentEvRow(row);

const normalizeEffectiveDate = (value) => (value ? String(value).slice(0, 10) : '');

/** Rate or effective-from changes must POST a new row (backend auto-closes the previous one). */
export const shouldPublishNewEvTariffPrice = (editingTariff, payload) => {
    if (!editingTariff) return false;
    const prevRate = Number(editingTariff.rate);
    const nextRate = Number(payload.rate);
    if (Number.isFinite(prevRate) && Number.isFinite(nextRate) && prevRate !== nextRate) {
        return true;
    }
    return normalizeEffectiveDate(editingTariff.effectiveFrom)
        !== normalizeEffectiveDate(payload.effectiveFrom);
};

/** Default table: one ACTIVE row per tariff code (prefer current / open-ended period). */
export const getActiveCurrentEvTariffRows = (rows) => {
    const active = excludeArchivedEvRows(rows);
    if (active.length === 0) return [];

    const byTariff = active.reduce((accumulator, row) => {
        const existing = accumulator.get(row.tariff);
        if (!existing) {
            accumulator.set(row.tariff, row);
            return accumulator;
        }
        const rowCurrent = isCurrentEvRow(row);
        const existingCurrent = isCurrentEvRow(existing);
        if (rowCurrent && !existingCurrent) {
            accumulator.set(row.tariff, row);
            return accumulator;
        }
        if (rowCurrent === existingCurrent) {
            const rowTs = row.effectiveFrom ? new Date(row.effectiveFrom).getTime() : 0;
            const existingTs = existing.effectiveFrom ? new Date(existing.effectiveFrom).getTime() : 0;
            if (rowTs > existingTs) accumulator.set(row.tariff, row);
        }
        return accumulator;
    }, new Map());

    return Array.from(byTariff.values()).sort((a, b) => {
        const aTs = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : 0;
        const bTs = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : 0;
        return bTs - aTs;
    });
};

export const getDisplayedEvTariffRows = (rows, filters) => {
    if (hasEvFilters(filters)) {
        return excludeArchivedEvRows(filterEvTariffRows(rows, filters));
    }
    return getActiveCurrentEvTariffRows(rows);
};

export const extractEvTariffData = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const payload = response.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.data && typeof payload.data === 'object') return [payload.data];
    return [];
};

const parseEvTariffRate = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

export const normalizeEvTariffRows = (apiRows) => {
    if (!Array.isArray(apiRows) || apiRows.length === 0) return [];

    return apiRows
        .filter((row) => row && row.tariff)
        .map((row, index) => {
            const mongoId = parseEvTariffMongoId(row._id) || parseEvTariffMongoId(row.id);
            const id = mongoId || `${row.tariff}-${row.effectiveFrom || index}`;
            const current = isCurrentEvRow(row);
            const archived = row.status === 'ARCHIVED';
            return {
                id: String(id),
                mongoId,
                tariff: row.tariff,
                rate: parseEvTariffRate(row.rate),
                effectiveFrom: row.effectiveFrom || null,
                effectiveTo: current ? null : (row.effectiveTo ?? null),
                operator: row.operator || '—',
                status: archived ? 'ARCHIVED' : (current ? 'ACTIVE' : 'ARCHIVED'),
                isCurrent: current,
                source: row.source || null,
            };
        })
        .sort((a, b) => {
            const aTs = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : 0;
            const bTs = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : 0;
            return bTs - aTs;
        });
};

/** Extract a 24-char Mongo ObjectId string for DELETE/PUT paths. */
export const parseEvTariffMongoId = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return /^[a-f\d]{24}$/i.test(trimmed) ? trimmed : null;
    }
    if (typeof value === 'object') {
        if (value.$oid) return String(value.$oid);
        if (typeof value.toString === 'function') {
            const asString = value.toString();
            if (/^[a-f\d]{24}$/i.test(asString)) return asString;
        }
    }
    return null;
};

export const excludeArchivedEvRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.filter((row) => row.status !== 'ARCHIVED');
};

export const getEvTariffErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 401) return 'Session expired. Redirecting to login…';
    if (status === 403) return 'You do not have permission to manage EV tariffs.';
    const apiErr = error?.response?.data?.error;
    if (apiErr?.message) return apiErr.message;
    if (error?.response?.data?.message) return error.response.data.message;
    return 'Unable to save EV tariff. Please try again.';
};
